/**
 * Compliance Item schemas — the heart of the system.
 *
 * Each compliance item is a scheduled service for a compliance client
 * (e.g. "UAE VAT Return Q3 2026", "Annual Accounts Filing 2026").
 * Items can be one-off or recurring (1, 3, 6, or 12 month cadence).
 *
 * Workflow: Pending → In Process → Ready for review → Reviewed →
 * Sent Back (bounce counter) → Sent to Client → Completed.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, pageInput, moneySchema, booleanQuerySchema } from './common';
import { JURISDICTION_CODES } from '../reference/jurisdictions';
import { ITEM_STATUSES, DATA_STATUSES, PERIOD_BUCKETS } from '../reference/statuses';
import { taxPeriodSchema } from './tax';

export const recurrenceFrequencySchema = z.enum(['WEEKLY', 'MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL']);
export type RecurrenceFrequency = z.infer<typeof recurrenceFrequencySchema>;

export const periodTypeSchema = z.enum(['QUARTER', 'MONTH', 'YEAR', 'OTHER']);
export type PeriodType = z.infer<typeof periodTypeSchema>;

export const itemStatusSchema = z.enum(ITEM_STATUSES);
export const dataStatusSchema = z.enum(DATA_STATUSES);
export const periodBucketSchema = z.enum(PERIOD_BUCKETS);

export const complianceItemOutputSchema = z.object({
  id: idSchema,
  clientId: idSchema,                          // compliance_client.id
  client: z.object({ id: idSchema, name: z.string() }).optional(),
  serviceType: z.string().min(1).max(100),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable(),
  status: itemStatusSchema,
  dataStatus: dataStatusSchema.nullable(),
  deadline: iso8601Schema.nullable(),
  dueDate: iso8601Schema.nullable(),
  dueDateOffsetDays: z.number().int().min(1).max(365).nullable(),
  nextDueAt: iso8601Schema.nullable(),
  completedAt: iso8601Schema.nullable(),
  // Recurrence
  isRecurring: z.boolean(),
  recurrenceFrequency: recurrenceFrequencySchema.nullable(),
  recurrenceSeriesId: idSchema.nullable(),      // groups instances of the same series
  sentBackCount: z.number().int().min(0),
  // Period (e.g. "Q3", "2026-07")
  periodType: periodTypeSchema.nullable(),
  periodValue: z.string().max(20).nullable(),
  taxPeriod: taxPeriodSchema.nullable().optional(),
  // Assignment
  assignee1Id: idSchema.nullable(),
  assignee2Id: idSchema.nullable(),
  managerId: idSchema.nullable(),
  remarks: z.string().max(2000).nullable(),
  estimatedHours: z.number().positive().nullable(),
  estimatedFee: moneySchema.nullable(),
  /** OPS 3.0b: evidence files filed against this item in the unified store (indicator only — never gates completion). */
  evidenceCount: z.number().int().nonnegative(),
  /** Documents Phase 3: item was auto-created by an expiring client document (the document's renewalItemId points here). */
  documentRenewal: z.boolean(),
  documentRenewalExpiryDate: iso8601Schema.nullable().optional(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type ComplianceItemOutput = z.infer<typeof complianceItemOutputSchema>;

export const createComplianceItemInputSchema = z.object({
  crmClientId: idSchema,
  serviceType: z.string().min(1).max(100),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable().optional(),
  deadline: iso8601Schema.nullable().optional(),
  dueDate: iso8601Schema.nullable().optional(),
  dueDateOffsetDays: z.number().int().min(1).max(365).nullable().optional(),
  status: itemStatusSchema.default('Pending'),
  dataStatus: dataStatusSchema.nullable().optional(),
  isRecurring: z.boolean().default(false),
  recurrenceFrequency: recurrenceFrequencySchema.nullable().optional(),
  periodType: periodTypeSchema.nullable().optional(),
  periodValue: z.string().max(20).nullable().optional(),
  assignee1Id: idSchema.nullable().optional(),
  assignee2Id: idSchema.nullable().optional(),
  managerId: idSchema.nullable().optional(),
  estimatedHours: z.number().positive().nullable().optional(),
  estimatedFee: moneySchema.nullable().optional(),
  remarks: z.string().max(2000).nullable().optional(),
});
export type CreateComplianceItemInput = z.infer<typeof createComplianceItemInputSchema>;

export const updateComplianceItemInputSchema = createComplianceItemInputSchema.partial().extend({
  status: itemStatusSchema.optional(),
  dataStatus: dataStatusSchema.nullable().optional(),
  dueDateOffsetDays: z.number().int().min(1).max(365).nullable().optional(),
  sentBackCount: z.number().int().min(0).optional(),
});
export type UpdateComplianceItemInput = z.infer<typeof updateComplianceItemInputSchema>;

export const completeItemInputSchema = z.object({
  actualHours: z.number().nonnegative().optional(),
  actualFee: moneySchema.optional(),
  remarks: z.string().max(2000).optional(),
  attachments: z.array(z.string().uuid()).max(10).optional(),
});
export type CompleteItemInput = z.infer<typeof completeItemInputSchema>;

export const listComplianceItemsInputSchema = pageInput.extend({
  clientId: idSchema.optional(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).optional(),
  serviceType: z.string().optional(),
  /** Free-text: client name/company, serviceType, jurisdiction, periodValue, remarks. */
  search: z.string().max(200).optional(),
  status: itemStatusSchema.optional(),
  dataStatus: dataStatusSchema.optional(),
  periodBucket: periodBucketSchema.optional(),
  assignee1Id: idSchema.optional(),
  assignee2Id: idSchema.optional(),
  assigneeId: idSchema.optional(),
  managerId: idSchema.optional(),
  dueBefore: iso8601Schema.optional(),
  dueAfter: iso8601Schema.optional(),
  dueDateBefore: iso8601Schema.optional(),
  dueDateAfter: iso8601Schema.optional(),
  /** When true, only items without a filing due date. */
  noDueDate: booleanQuerySchema.optional(),
  /** When true, only open (non-Completed) items — composes with other filters. */
  open: booleanQuerySchema.optional(),
  /** When true, the dashboard "blocked & stalled" definition: open items that are
   *  waiting on the client (PendingRequest/DataRequested), SentBack, or stale
   *  (no update in 7+ days). */
  blocked: booleanQuerySchema.optional(),
  sortBy: z.enum(['serviceType', 'client', 'jurisdiction', 'status', 'dataStatus', 'deadline', 'dueDate']).default('deadline'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
  /** When true, filter to compliance items assigned to the current user. */
  assignedToMe: booleanQuerySchema.optional(),
  /** When true, includes soft-deleted. Default false. */
  all: booleanQuerySchema.optional(),
});
export type ListComplianceItemsInput = z.infer<typeof listComplianceItemsInputSchema>;

export const bulkDeleteComplianceItemsInputSchema = z.object({
  ids: z.array(idSchema).min(1).max(500),
  password: z.string().min(8),
});
export type BulkDeleteComplianceItemsInput = z.infer<typeof bulkDeleteComplianceItemsInputSchema>;

/** Bulk import compliance items. */
export const importComplianceItemsRowSchema = z.object({
  clientName: z.string().min(1).max(200),
  serviceType: z.string().min(1).max(100),
  jurisdiction: z.string().max(10).nullable().optional(),
  deadline: z.string().max(50).nullable().optional(),
  dueDateOffsetDays: z.string().max(10).nullable().optional(),
  status: z.string().max(50).nullable().optional(),
  dataStatus: z.string().max(50).nullable().optional(),
  isRecurring: z.string().max(10).nullable().optional(),
  recurrenceFrequency: z.string().max(20).nullable().optional(),
  periodType: z.string().max(20).nullable().optional(),
  periodValue: z.string().max(20).nullable().optional(),
  assignee1Name: z.string().max(200).nullable().optional(),
  assignee1Email: z.string().email().max(254).nullable().optional(),
  assignee2Name: z.string().max(200).nullable().optional(),
  assignee2Email: z.string().email().max(254).nullable().optional(),
  managerName: z.string().max(200).nullable().optional(),
  managerEmail: z.string().email().max(254).nullable().optional(),
  estimatedHours: z.string().max(20).nullable().optional(),
  estimatedFeeAmount: z.string().max(30).nullable().optional(),
  estimatedFeeCurrency: z.string().max(3).nullable().optional(),
  remarks: z.string().max(2000).nullable().optional(),
});
export type ImportComplianceItemsRow = z.infer<typeof importComplianceItemsRowSchema>;

export const importComplianceItemsInputSchema = z.object({
  rows: z.array(importComplianceItemsRowSchema).min(1).max(500),
  dryRun: z.boolean().default(false),
});
export type ImportComplianceItemsInput = z.infer<typeof importComplianceItemsInputSchema>;

export const importComplianceItemsErrorSchema = z.object({
  row: z.number().int().min(0),
  clientName: z.string().optional(),
  serviceType: z.string().optional(),
  field: z.string().optional(),
  message: z.string(),
});

export const importComplianceItemsOutputSchema = z.object({
  created: z.number().int().min(0),
  failed: z.number().int().min(0),
  errors: z.array(importComplianceItemsErrorSchema),
});
export type ImportComplianceItemsOutput = z.infer<typeof importComplianceItemsOutputSchema>;

/** Item workflow actions: send back, mark complete, etc. */
export const itemActionSchema = z.enum([
  'enable',
  'pause',
  'resume',
  'remove',
  'change_cadence',
  'send_back',
  'mark_completed',
  'send_to_client',
  'ready_for_review',
]);
export type ItemAction = z.infer<typeof itemActionSchema>;